import React, { createContext, useState, useContext, useEffect } from 'react';
import cognitoService from '../services/cognitoService';

// Create auth context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkUserLoggedIn = async () => {
      setLoading(true);
      try {
        // Try to get the current authenticated user from Cognito
        const cognitoUser = await cognitoService.getCurrentUser();
        if (cognitoUser) {
          try {
            // Get session to access tokens and user attributes
            const session = await cognitoService.getCurrentSession();
            const idToken = session.tokens?.idToken;
            const userAttributes = idToken?.payload || {};
            
            // Create a user object from Cognito attributes
            const user = {
              id: cognitoUser.username,
              email: userAttributes.email,
              name: userAttributes.name || userAttributes.given_name || cognitoUser.username,
              // Add any other attributes you need
            };
            
            setCurrentUser(user);
          } catch (sessionErr) {
            console.error('Failed to fetch user session:', sessionErr);
            setCurrentUser(null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user:', err);
        // User is not authenticated with Cognito
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkUserLoggedIn();
  }, []);

  // Logout user
  const logout = async () => {
    try {
      await cognitoService.signOut();
      setCurrentUser(null);
    } catch (err) {
      console.error('Logout error:', err);
      // Still clear the user from state even if there's an error
      setCurrentUser(null);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
